import { child, push, ref, set, update } from "firebase/database";
import { useMemo } from "react";
import { useListVals, useObjectVal } from "react-firebase-hooks/database";
import { fbDatabase } from "../firebase";
import { decryptFieldOrPlaceholder, encryptField, getPrimarySessionPassphrase, useSessionPassphrases } from "../util/encryption";
import { categoryTransform } from "./category.types";
import type { CategoryData, Color } from "./category.types";

export * from "./category.types";

const categoryOptions = {
  keyField: 'uid',
  transform: categoryTransform,
};

export function refCategoriesRoot(boardUid: string) { return ref(fbDatabase, `categories/${boardUid}`) }
export function refCategories(boardUid: string, sessionUid: string) { return child(refCategoriesRoot(boardUid), sessionUid) }
export function refCategory(boardUid: string, sessionUid: string, categoryUid: string) { return child(refCategories(boardUid, sessionUid), categoryUid) }

function decryptCategoryFields(cat: CategoryData, passphrases: string[], sessionUid: string): CategoryData {
  const title = decryptFieldOrPlaceholder(cat.title, passphrases, sessionUid);
  const description = cat.description != null ? decryptFieldOrPlaceholder(cat.description, passphrases, sessionUid) : cat.description;
  if (title === cat.title && description === cat.description) return cat;
  return { ...cat, title, description };
}

export function useCategories(boardUid: string, sessionUid: string) {
  const passphrases = useSessionPassphrases(sessionUid);
  const [categories, loading, error] = useListVals<CategoryData>(refCategories(boardUid, sessionUid), categoryOptions);
  const decrypted = useMemo(() => {
    if (!categories) return categories;
    return categories.map(cat => decryptCategoryFields(cat, passphrases, sessionUid));
  }, [categories, passphrases, sessionUid]);
  return [decrypted, loading, error] as const;
}
export function useCategory(boardUid: string, sessionUid: string, categoryUid: string) {
  const passphrases = useSessionPassphrases(sessionUid);
  const [category, loading, error] = useObjectVal<CategoryData>(refCategory(boardUid, sessionUid, categoryUid), categoryOptions);
  const decrypted = useMemo(() => {
    if (!category) return category;
    return decryptCategoryFields(category, passphrases, sessionUid);
  }, [category, passphrases, sessionUid]);
  return [decrypted, loading, error] as const;
}

export const createCategory: (
  boardUid: string,
  sessionUid: string,
  title: string,
  description: string | null,
  color: Color,
) => Promise<string | null> = async (boardUid, sessionUid, title, description, color) => {
  const newCategoryRef = await push(refCategories(boardUid, sessionUid));
  if (!newCategoryRef.key) return null;

  const passphrase = getPrimarySessionPassphrase(sessionUid);
  const encTitle = passphrase ? encryptField(title, passphrase) : title;
  const encDescription = passphrase && description ? encryptField(description, passphrase) : description;

  await set(newCategoryRef, {
    boardUid: boardUid,
    sessionUid: sessionUid,
    title: encTitle,
    description: encDescription,
    color: color,
  });

  return newCategoryRef.key;
}

export const editCategory: (
  categoryData: CategoryData,
  title: string,
  description: string | null,
  color: Color,
) => Promise<void> = async (categoryData, title, description, color) => {
  const passphrase = getPrimarySessionPassphrase(categoryData.sessionUid);
  const encTitle = passphrase ? encryptField(title, passphrase) : title;
  const encDescription = passphrase && description ? encryptField(description, passphrase) : description;

  await update(refCategory(categoryData.boardUid, categoryData.sessionUid, categoryData.uid), {
    title: encTitle,
    description: encDescription,
    color: color,
  });
}

export const showCategory: (
  categoryData: CategoryData,
) => Promise<void> = async (categoryData) => {
  await update(refCategory(categoryData.boardUid, categoryData.sessionUid, categoryData.uid), {
    visible: true,
  })
}

export const hideCategory: (
  categoryData: CategoryData,
) => Promise<void> = async (categoryData) => {
  await update(refCategory(categoryData.boardUid, categoryData.sessionUid, categoryData.uid), {
    visible: false,
  })
}
